import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

/**
 * Route guard that checks the user's onboarding state and redirects accordingly.
 * Wraps the main app routes — setup routes must NOT be inside this guard.
 *
 * For existing users who already completed setup before onboarding_status existed,
 * we check for completed ad accounts as a fallback and auto-upgrade their status.
 */
export const OnboardingGuard = () => {
  const { user } = useAuth();
  const location = useLocation();

  // Check for Meta OAuth token
  const { data: hasMetaToken, isLoading: loadingToken } = useQuery({
    queryKey: ["onboarding-meta-token", user?.id],
    queryFn: async () => {
      const { data } = await supabase.from("meta_oauth_tokens").select("id").eq("user_id", user!.id).maybeSingle();
      return !!data;
    },
    enabled: !!user?.id,
  });

  // Check onboarding status from users table
  const { data: onboardingStatus, isLoading: loadingProfile } = useQuery({
    queryKey: ["onboarding-profile", user?.id],
    queryFn: async () => {
      const { data } = await supabase.from("users").select("onboarding_status").eq("id", user!.id).single();
      return (data as any)?.onboarding_status as string | undefined;
    },
    enabled: !!user?.id,
  });

  // Safety net: check if user has any completed ad accounts
  // This handles existing users who were set up before onboarding_status existed
  const { data: hasCompletedAccounts, isLoading: loadingAccounts } = useQuery({
    queryKey: ["onboarding-completed-accounts", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("ad_accounts")
        .select("id")
        .eq("user_id", user!.id)
        .eq("setup_status", "completed")
        .limit(1);
      return (data?.length ?? 0) > 0;
    },
    enabled: !!user?.id,
  });

  const isLoading = loadingToken || loadingProfile || loadingAccounts;

  // Full-screen loader while checking state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  // Safety net: existing users with completed accounts are always allowed through.
  // Auto-upgrade their onboarding_status in the background.
  if (hasCompletedAccounts && onboardingStatus !== "COMPLETED") {
    supabase
      .from("users")
      .update({ onboarding_status: "COMPLETED" } as any)
      .eq("id", user!.id)
      .then(() => {}); // fire-and-forget

    // Redirect to /settings if landing on root, otherwise render normally
    if (location.pathname === "/") {
      return <Navigate to="/settings" replace />;
    }
    return <Outlet />;
  }

  if (onboardingStatus === "COMPLETED") {
    // Redirect to /settings if landing on root, otherwise render normally
    if (location.pathname === "/") {
      return <Navigate to="/settings" replace />;
    }
    return <Outlet />;
  }

  // 1. No Meta token → go connect
  if (!hasMetaToken) {
    return <Navigate to="/setup/connect" replace />;
  }

  // 2. Meta connected but accounts not selected
  if (!onboardingStatus || onboardingStatus === "NOT_STARTED" || onboardingStatus === "META_CONNECTED") {
    return <Navigate to="/setup/select-accounts" replace />;
  }

  // 3. Accounts selected but wizard not finished
  if (onboardingStatus === "ACCOUNTS_SELECTED") {
    return <Navigate to="/setup/wizard" replace />;
  }

  // Fallback: render the app
  return <Outlet />;
};
